import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState, type FormEvent } from "react";
import { ApiError, admin, type AdminVocabRow, type VocabPayload } from "../../lib/api";
import { useAdminVocabStore, type MutationResult } from "../../state/adminVocabStore";

type Mode = "create" | "edit";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: Mode;
  /** Pre-populated values when editing. Ignored for create. */
  initial?: AdminVocabRow;
}

interface FormState {
  english: string;
  german: string;
  abbreviation: string;
  examples: string;
}

function rowToForm(row: AdminVocabRow): FormState {
  return {
    english: row.english,
    german: row.german,
    abbreviation: row.abbreviation ?? "",
    examples: row.examples.join("\n"),
  };
}

function blankForm(): FormState {
  return { english: "", german: "", abbreviation: "", examples: "" };
}

function parseExamples(raw: string): string[] {
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * Shared Add/Edit dialog for vocab rows. Same form fields in both modes —
 * the only difference is whether the english input is read-only (edit) or
 * the primary input the user fills in (create). Examples are entered as
 * one-per-line in a textarea; we split on newline server-bound.
 */
export function VocabFormDialog({ open, onOpenChange, mode, initial }: Props): JSX.Element {
  const createFn = useAdminVocabStore((s) => s.create);
  const updateFn = useAdminVocabStore((s) => s.update);

  const [form, setForm] = useState<FormState>(blankForm);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [aiState, setAiState] = useState<
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | { kind: "success" }
  >({ kind: "idle" });

  // Reset the form whenever the dialog reopens or the editable row changes.
  useEffect(() => {
    if (!open) return;
    setForm(mode === "edit" && initial ? rowToForm(initial) : blankForm());
    setErrorMessage(null);
    setAiState({ kind: "idle" });
  }, [open, mode, initial]);

  async function onGenerateExamples() {
    const english = form.english.trim();
    const german = form.german.trim();
    if (english.length === 0 || german.length === 0) {
      setAiState({
        kind: "error",
        message: "Bitte zuerst englischen und deutschen Begriff eintragen.",
      });
      return;
    }
    setAiState({ kind: "loading" });
    try {
      const { examples } = await admin.vocab.generateExamples(english, german);
      setForm((f) => ({ ...f, examples: examples.join("\n") }));
      setAiState({ kind: "success" });
    } catch (err) {
      const message = (() => {
        if (err instanceof ApiError) {
          if (err.message === "ai_disabled") return "KI-Generator nicht konfiguriert (kein API-Key).";
          if (err.kind === "rate_limited") return "Zu viele KI-Anfragen. Bitte später erneut.";
          if (err.message === "upstream_error") return "KI-Server hat nicht geantwortet. Erneut versuchen.";
          if (err.message === "bad_response") return "KI-Antwort konnte nicht verarbeitet werden. Erneut versuchen.";
          if (err.kind === "network") return "Keine Verbindung.";
        }
        return "Fehler bei der KI-Generierung.";
      })();
      setAiState({ kind: "error", message });
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMessage(null);
    setSubmitting(true);
    const payload: VocabPayload = {
      german: form.german,
      abbreviation: form.abbreviation.trim().length > 0 ? form.abbreviation : null,
      examples: parseExamples(form.examples),
    };
    let result: MutationResult;
    if (mode === "create") {
      result = await createFn({ ...payload, english: form.english });
    } else {
      result = await updateFn(initial!.english, payload);
    }
    setSubmitting(false);
    if (result.ok) {
      onOpenChange(false);
      return;
    }
    setErrorMessage(result.message);
  }

  const title = mode === "create" ? "Neues Wort hinzufügen" : "Wort bearbeiten";
  const submitLabel = mode === "create" ? "Speichern + ans Quiz weitergeben" : "Speichern";

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 animate-fade-in" />
        <Dialog.Content
          data-testid="vocab-form-dialog"
          className="
            fixed z-50 bg-elevated shadow-modal p-5 space-y-4
            max-h-[90dvh] overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]
            left-0 right-0 bottom-0 rounded-t-sheet border-t border-outline-strong animate-sheet-up
            sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:right-auto sm:w-[480px] sm:max-h-[85vh]
            sm:-translate-x-1/2 sm:-translate-y-1/2
            sm:rounded-sheet sm:border sm:animate-slide-up
            safe-bottom
          "
        >
          <div className="sm:hidden mx-auto -mt-1 mb-2 h-1.5 w-10 rounded-full bg-outline" aria-hidden />

          <Dialog.Title className="text-lg font-bold text-fg">{title}</Dialog.Title>
          <Dialog.Description className="sr-only">
            {mode === "create"
              ? "Füge einen neuen englischen Begriff mit deutscher Übersetzung und mindestens 2 Beispielen hinzu."
              : "Bearbeite die deutsche Übersetzung, Abkürzung und Beispiele dieses Eintrags."}
          </Dialog.Description>

          <form onSubmit={onSubmit} className="space-y-3" data-testid="vocab-form">
            <Field
              label="Englisch *"
              hint={
                mode === "edit"
                  ? "Identity-Key — kann nicht geändert werden. Bei Tippfehlern: neuen Eintrag anlegen + alten löschen."
                  : "Wird beim Speichern auf Kleinschreibung normalisiert. Duplikate werden abgelehnt."
              }
            >
              <input
                type="text"
                value={form.english}
                onChange={(e) => setForm((f) => ({ ...f, english: e.target.value }))}
                readOnly={mode === "edit"}
                required
                maxLength={100}
                autoCapitalize="none"
                spellCheck={false}
                data-testid="vocab-english"
                className={`w-full min-h-[44px] bg-bg border border-outline rounded-lg px-3 text-sm text-fg focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 ${mode === "edit" ? "opacity-70 cursor-not-allowed" : ""}`}
                placeholder={mode === "create" ? "z. B. approach control" : undefined}
              />
            </Field>

            <Field label="Deutsch *">
              <input
                type="text"
                value={form.german}
                onChange={(e) => setForm((f) => ({ ...f, german: e.target.value }))}
                required
                maxLength={200}
                data-testid="vocab-german"
                className="w-full min-h-[44px] bg-bg border border-outline rounded-lg px-3 text-sm text-fg focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                placeholder="z. B. Anflugkontrolle"
              />
            </Field>

            <Field label="Abkürzung" hint="optional, max 16 Zeichen (z. B. APP, ATC, IFR)">
              <input
                type="text"
                value={form.abbreviation}
                onChange={(e) => setForm((f) => ({ ...f, abbreviation: e.target.value }))}
                maxLength={16}
                data-testid="vocab-abbreviation"
                className="w-full min-h-[44px] bg-bg border border-outline rounded-lg px-3 text-sm text-fg focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
              />
            </Field>

            <Field
              label="Beispiele *"
              hint="Mindestens 2, maximal 5 Beispielsätze. 1 pro Zeile, max 200 Zeichen je Zeile."
            >
              <textarea
                value={form.examples}
                onChange={(e) => setForm((f) => ({ ...f, examples: e.target.value }))}
                required
                rows={5}
                data-testid="vocab-examples"
                className="w-full min-h-[120px] bg-bg border border-outline rounded-lg px-3 py-2 text-sm text-fg focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 leading-relaxed font-sans"
                placeholder={"Approach control vectored us for the ILS.\nContact approach on 119.4."}
              />
              <div className="flex items-center gap-2 mt-1.5">
                <button
                  type="button"
                  onClick={() => void onGenerateExamples()}
                  disabled={aiState.kind === "loading"}
                  data-testid="vocab-ai-generate"
                  className="min-h-[32px] px-2.5 rounded-lg text-xs font-semibold text-accent border border-accent/40 hover:bg-accent-soft disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  {aiState.kind === "loading" ? "⏳ Generiere…" : "✨ Beispiele mit KI generieren"}
                </button>
                <span className="text-[11px] text-fg-faint">
                  2 Beispiele · Aviation-Kontext · Claude haiku
                </span>
              </div>
              {aiState.kind === "error" && (
                <p
                  role="alert"
                  data-testid="vocab-ai-error"
                  className="mt-1.5 text-xs text-danger"
                >
                  {aiState.message}
                </p>
              )}
              {aiState.kind === "success" && (
                <p
                  data-testid="vocab-ai-success"
                  className="mt-1.5 text-xs text-success"
                >
                  ✓ Beispiele eingefügt — bei Bedarf editieren oder erneut generieren.
                </p>
              )}
            </Field>

            {errorMessage && (
              <div
                role="alert"
                data-testid="vocab-form-error"
                className="bg-danger-soft border border-danger/40 rounded-lg px-3 py-2 text-sm text-danger"
              >
                {errorMessage}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 pt-1">
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={submitting}
                  data-testid="vocab-form-cancel"
                  className="min-h-[48px] rounded-xl bg-bg/40 border border-outline text-fg text-sm font-semibold hover:bg-card disabled:opacity-60"
                >
                  Abbrechen
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={submitting}
                data-testid="vocab-form-submit"
                className="min-h-[48px] rounded-xl bg-accent text-accent-on text-sm font-semibold hover:bg-accent-hover disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "…" : submitLabel}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium text-fg-soft">{label}</span>
      {children}
      {hint && <span className="block text-xs text-fg-faint leading-snug">{hint}</span>}
    </label>
  );
}
